<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createStorjConfig, storjGateway } from "./fs";
  import { makeFileSystemStorj, testStorjConnection } from "./fs-storj";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createStorjConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.access.trim() != "" && config.secret.trim() != "" && config.bucket.trim() != "" && !testingConnection,
  );

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing storj connection");
    try {
      config.access = config.access.trim();
      config.secret = config.secret.trim();
      config.bucket = config.bucket.trim();
      config.endpoint = config.endpoint.trim() || storjGateway;
      await testStorjConnection(config);
      if (config.name.trim() == "") {
        config.name = config.bucket;
      } else {
        config.name = config.name.trim();
      }
      let fs = makeFileSystemStorj(config);
      let who = config.name;
      telegramNotify(`New Storj connection for ${who}`);
      captureEvent("storj-login", { bucket: config.bucket });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Storj">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-key">Access key:</label>
      <input
        type="text"
        id="input-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.access}
      />

      <label class="mt-3" for="input-secret">Secret key:</label>
      <input
        type="password"
        id="input-secret"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.secret}
      />

      <label class="mt-3" for="input-bucket">Bucket:</label>
      <input
        type="text"
        id="input-bucket"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.bucket}
      />

      <label class="mt-3" for="input-endpoint">Endpoint:</label>
      <input
        type="text"
        id="input-endpoint"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.endpoint}
      />

      <div class="mt-4 text-sm">
        <a
          href="https://storj.dev/dcs/api/s3/credentials"
          class="text-blue-600 underline"
          target="_blank"
          rel="noreferrer">S3 credentials</a
        >
      </div>

      {#if testingConnection}
        <div class="text-sm text-gray-500 mt-2 mb-2">testing storj settings...</div>
      {/if}

      {#if errorMessage}
        <div class="mt-2 mb-2 text-red-500 font-medium break-all w-72">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Storj</button>
    </div>
  {/snippet}
</DialogBase>
